/**
 * Accessibility Helpers Utility
 * Provides keyboard handlers and ARIA helpers for WCAG compliance
 * Issue #9: Empty KeyDown Handler (Accessibility)
 */

/**
 * Creates a keyboard event handler for interactive elements
 * Supports Enter and Space keys as per WCAG guidelines
 * @param {Function} callback - Function to call on activation
 * @param {Object} options - Configuration options
 * @returns {Function} - Event handler function
 */
export const createKeyboardHandler = (callback, options = {}) => {
    const {
        preventDefault = true,
        stopPropagation = false,
        keys = ["Enter", " "], // Space is ' ' in key events
    } = options;

    return (event) => {
        if (keys.includes(event.key)) {
            if (preventDefault) {
                event.preventDefault();
            }
            if (stopPropagation) {
                event.stopPropagation();
            }
            callback(event);
        }
    };
};

/**
 * Props to make a div behave like a button
 * Use spread syntax: {...getButtonProps(onClick, ariaLabel)}
 * @param {Function} onClick - Click handler function
 * @param {string} ariaLabel - Accessible label for screen readers
 * @returns {Object} - Props object to spread on element
 */
export const getButtonProps = (onClick, ariaLabel) => ({
    role: "button",
    tabIndex: 0,
    onClick,
    onKeyDown: createKeyboardHandler(onClick),
    "aria-label": ariaLabel,
});

/**
 * Creates keyboard handler specifically for Enter key
 * Useful for form submission scenarios
 * @param {Function} callback - Function to call on Enter
 * @returns {Function} - Event handler function
 */
export const createEnterKeyHandler = (callback) => {
    return createKeyboardHandler(callback, { keys: ["Enter"] });
};

/**
 * Creates keyboard handler specifically for Escape key
 * Useful for closing modals/drawers
 * @param {Function} callback - Function to call on Escape
 * @returns {Function} - Event handler function
 */
export const createEscapeKeyHandler = (callback) => {
    return createKeyboardHandler(callback, { keys: ["Escape"], preventDefault: false });
};

export default {
    createKeyboardHandler,
    getButtonProps,
    createEnterKeyHandler,
    createEscapeKeyHandler,
};
